import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { payrollRegistry, type AccrualRegistryItem, type AccrualKind } from '@/api/payroll_registry';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const KIND_LABEL: Record<AccrualKind, string> = {
  MAIN: 'Основна', ADVANCE: 'Аванс', VACATION: 'Відпустка',
  SICK: 'Лікарняний', COMPENSATION: 'Компенсація', CIVIL: 'ЦПУ',
};
const KIND_BADGE: Record<AccrualKind, { bg: string; fg: string }> = {
  MAIN: { bg: '#dcfce7', fg: '#166534' },
  ADVANCE: { bg: '#dbeafe', fg: '#1e40af' },
  VACATION: { bg: '#fef9c3', fg: '#854d0e' },
  SICK: { bg: '#fee2e2', fg: '#991b1b' },
  COMPENSATION: { bg: '#f3e8ff', fg: '#6b21a8' },
  CIVIL: { bg: '#ffedd5', fg: '#9a3412' },
};
// Маршрут редактора за рядком. Для відпустки/лікарняного — на РОЗРАХУНОК
// (calculation_id), якщо є; інакше на наказ/лист.
function routeForRow(r: AccrualRegistryItem): string {
  switch (r.route) {
    case 'payroll': return `/payrolls/${r.id}`;
    case 'vacation': return r.calculation_id
      ? `/hr/vacation-calculations/${r.calculation_id}` : `/hr/vacation-orders/${r.id}`;
    case 'sick': return `/hr/sick-leaves/${r.id}`;
    case 'compensation': return `/hr/vacation-compensations/${r.id}`;
  }
}

// Випадаюче меню «Створити». Борги — НЕ вид нарахування: окремий пункт меню
// «Борги по ЗП» (рішення власника 2026-07-05).
const CREATE_OPTIONS: Array<{ kind: AccrualKind; label: string; to: string }> = [
  { kind: 'MAIN', label: 'Нарахування ЗП основне', to: '/payrolls/new?kind=MAIN' },
  { kind: 'ADVANCE', label: 'Нарахування авансу', to: '/payrolls/new?kind=ADVANCE' },
  { kind: 'CIVIL', label: 'Нарахування по ЦПУ', to: '/payrolls/new?kind=CIVIL' },
  { kind: 'VACATION', label: 'Нарахування відпускних', to: '/hr/vacation-calculations/new' },
  { kind: 'SICK', label: 'Нарахування лікарняного', to: '/hr/sick-leaves/new' },
  { kind: 'COMPENSATION', label: 'Компенсація невикор. відпустки', to: '/hr/vacation-compensations/new' },
];

export default function AccrualsRegistryPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [orgId, setOrgId] = useState('');
  const [kind, setKind] = useState<'' | AccrualKind>('');
  const [periodYear, setPeriodYear] = useState<number | ''>(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState<number | ''>(now.getMonth() + 1);
  const [showCreate, setShowCreate] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  // За замовчуванням orgId='' = «Усі» → виводимо нарахування по ВСIХ org одразу.
  const list = useQuery({
    queryKey: ['accruals-registry', orgId, kind, periodYear, periodMonth],
    queryFn: () => payrollRegistry.accruals({
      organization_id: orgId || undefined,
      kind: kind || undefined,
      period_year: periodYear || undefined,
      period_month: periodMonth || undefined,
    }),
  });

  const cols = useMemo<ColumnDef<AccrualRegistryItem>[]>(() => [
    { key: 'document_number', header: '№', width: 120, importBadge: true,
      sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'kind', header: 'Вид', width: 130,
      sortValue: (r) => r.kind, cell: (r) => (
        <span style={{ background: KIND_BADGE[r.kind].bg, color: KIND_BADGE[r.kind].fg,
          borderRadius: 4, padding: '1px 6px', fontSize: '0.78rem', fontWeight: 600 }}>
          {KIND_LABEL[r.kind]}
        </span>
      ) },
    { key: 'document_date', header: 'Дата', width: 150,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '' },
    { key: 'period', header: 'Період', width: 130,
      sortValue: (r) => (r.period_year ?? 0) * 100 + (r.period_month ?? 0),
      cell: (r) => r.period_month ? `${MONTHS[r.period_month]} ${r.period_year}` : '—' },
    { key: 'organization', header: 'Орг', width: 160,
      sortValue: (r) => orgName.get(r.organization_id) ?? '',
      cell: (r) => orgName.get(r.organization_id) ?? '—' },
    { key: 'employee', header: 'Працівник', width: 190,
      sortValue: (r) => r.employee_name ?? '',
      cell: (r) => r.employee_name ?? '(усі)' },
    { key: 'net', header: 'До виплати', width: 120, align: 'right',
      sortValue: (r) => Number(r.net), cell: (r) => fmt2(r.net) },
    { key: 'paid', header: 'Виплачено', width: 110, align: 'right',
      sortValue: (r) => r.paid != null ? Number(r.paid) : -1,
      cell: (r) => r.paid != null ? fmt2(r.paid) : '—' },
    { key: 'debt', header: 'Борг', width: 110, align: 'right',
      sortValue: (r) => r.debt != null ? Number(r.debt) : -1,
      cell: (r) => r.debt == null ? '—'
        : Number(r.debt) > 0
          ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>{fmt2(r.debt)}</span>
          : <span style={{ color: '#15803d' }}>0,00</span> },
    { key: 'posted', header: 'Проведено', width: 100, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні') },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нарахування виплат</h2>
        <div style={{ position: 'relative' }}>
          <button className="primary" onClick={() => setShowCreate((s) => !s)}>
            + Створити ▾
          </button>
          {showCreate && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
              background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 240, overflow: 'hidden',
            }}>
              {CREATE_OPTIONS.map((o) => (
                <button key={o.kind} onClick={() => { setShowCreate(false); navigate(o.to); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    background: 'none', padding: '0.5rem 0.75rem', cursor: 'pointer', fontWeight: 500 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 220, margin: 0 }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">Усі</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 160, margin: 0 }}>
          <label>Вид</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as '' | AccrualKind)}>
            <option value="">— усі —</option>
            <option value="MAIN">Основна</option>
            <option value="ADVANCE">Аванс</option>
            <option value="VACATION">Відпустка</option>
            <option value="SICK">Лікарняний</option>
            <option value="COMPENSATION">Компенсація</option>
            <option value="CIVIL">ЦПУ</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 2 }}>
              <option value="">— усі —</option>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 1 }}>
              <option value="">— усі —</option>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <DataTable<AccrualRegistryItem>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => `${r.route}-${r.id}`}
        loading={list.isLoading}
        emptyText="Немає нарахувань."
        onRowDoubleClick={(r) => navigate(routeForRow(r))}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
